import { useState } from 'react';
import { Plus, Wrench, CreditCard as Edit2, Trash2, X, Calculator } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAgriEquipment } from '../../hooks/useAgriculture';
import { computeMonthlyLoan, formatEur, formatNum } from '../../utils/agriEngine';
import type { AgriEquipment } from '../../utils/agriEngine';

function EquipmentFormModal({ equipment, onClose, onSaved }: { equipment?: AgriEquipment | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: equipment?.name || '',
    purchase_price: String(equipment?.purchase_price || ''),
    equity_amount: String(equipment?.equity_amount || ''),
    loan_duration_years: String(equipment?.loan_duration_years || 7),
    purchase_date: equipment?.purchase_date || '',
    status: (equipment?.status || 'projet') as 'projet' | 'finance' | 'acquis',
    notes: equipment?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }));

  const purchasePrice = parseFloat(form.purchase_price) || 0;
  const equity = parseFloat(form.equity_amount) || 0;
  const financed = Math.max(0, purchasePrice - equity);
  const years = parseInt(form.loan_duration_years) || 7;
  const monthly = computeMonthlyLoan(financed, years);

  async function handleSave() {
    if (!form.name || !form.purchase_price) return;
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }
    const payload = {
      user_id: user.id, name: form.name, purchase_price: purchasePrice,
      equity_amount: equity, financed_amount: financed,
      loan_duration_years: years, monthly_payment: monthly,
      purchase_date: form.purchase_date || null, status: form.status, notes: form.notes || null,
    };
    const { error } = equipment
      ? await supabase.from('agri_equipment').update(payload).eq('id', equipment.id)
      : await supabase.from('agri_equipment').insert(payload);
    setSaving(false);
    if (!error) onSaved();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{equipment ? 'Modifier le matériel' : 'Nouveau matériel'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Désignation *</label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Tracteur 180CV, Moissonneuse..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix d'achat (€) *</label>
              <input type="number" min="0" step="1000" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Apport personnel (€)</label>
              <input type="number" min="0" step="1000" value={form.equity_amount} onChange={e => set('equity_amount', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Durée emprunt (ans)</label>
              <select value={form.loan_duration_years} onChange={e => set('loan_duration_years', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm">
                {[3, 5, 7, 10, 12, 15].map(y => <option key={y} value={y}>{y} ans</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select value={form.status} onChange={e => set('status', e.target.value as 'projet' | 'finance' | 'acquis')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm">
                <option value="projet">Projet</option>
                <option value="finance">Financé</option>
                <option value="acquis">Acquis</option>
              </select>
            </div>
          </div>

          {financed > 0 && (
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Simulation de remboursement</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <p className="text-blue-600 text-xs">Montant financé</p>
                  <p className="font-bold text-blue-900">{formatEur(financed)}</p>
                </div>
                <div>
                  <p className="text-blue-600 text-xs">Mensualité</p>
                  <p className="font-bold text-blue-900">{formatEur(monthly)}/mois</p>
                </div>
                <div>
                  <p className="text-blue-600 text-xs">Coût total</p>
                  <p className="font-bold text-blue-900">{formatEur(monthly * years * 12)}</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date d'achat</label>
            <input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm resize-none" />
          </div>
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm">Annuler</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function EquipmentTab() {
  const { equipment, loading, refresh } = useAgriEquipment();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AgriEquipment | null>(null);

  const totalFinanced = equipment.filter(e => e.status !== 'projet').reduce((s, e) => s + e.financed_amount, 0);
  const totalMonthly = equipment.filter(e => e.status === 'finance').reduce((s, e) => s + e.monthly_payment, 0);

  async function handleDelete(id: string) {
    if (!confirm('Supprimer ce matériel ?')) return;
    await supabase.from('agri_equipment').delete().eq('id', id);
    refresh();
  }

  if (loading) return <div className="flex items-center justify-center py-16"><div className="animate-spin w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Matériel & investissements</h2>
          <p className="text-sm text-gray-500">{equipment.length} équipement{equipment.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium shadow-sm">
          <Plus className="w-4 h-4" />
          Nouveau matériel
        </button>
      </div>

      {totalMonthly > 0 && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <p className="text-xs text-blue-600 font-medium mb-1">Annuités totales en cours</p>
            <p className="text-2xl font-bold text-blue-900">{formatEur(totalMonthly)}<span className="text-sm font-normal text-blue-700">/mois</span></p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
            <p className="text-xs text-slate-600 font-medium mb-1">Capital total financé</p>
            <p className="text-2xl font-bold text-slate-900">{formatEur(totalFinanced)}</p>
          </div>
        </div>
      )}

      {equipment.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Wrench className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-gray-700 font-medium mb-2">Aucun matériel enregistré</h3>
          <p className="text-gray-400 text-sm mb-6">Simulez vos emprunts et suivez l'impact de vos investissements sur votre trésorerie</p>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium">
            <Plus className="w-4 h-4" />
            Ajouter un équipement
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {equipment.map(e => {
            const financed = e.financed_amount;
            const monthly = e.monthly_payment || computeMonthlyLoan(financed, e.loan_duration_years);
            const totalCost = monthly * e.loan_duration_years * 12;
            const interestCost = totalCost - financed;
            return (
              <div key={e.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{e.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          e.status === 'acquis' ? 'bg-green-100 text-green-700' :
                          e.status === 'finance' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'}`}>
                          {e.status === 'acquis' ? 'Acquis' : e.status === 'finance' ? 'Financé' : 'Projet'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {formatEur(e.purchase_price)} · Apport {formatEur(e.equity_amount)} · Emprunt {e.loan_duration_years} ans
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(e); setShowForm(true); }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(e.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500 font-medium">Montant financé</p>
                    <p className="text-base font-bold text-gray-800">{formatEur(financed)}</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3">
                    <p className="text-xs text-red-600 font-medium">Mensualité</p>
                    <p className="text-base font-bold text-red-900">{formatEur(monthly)}</p>
                    <p className="text-xs text-red-500">/mois</p>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-3">
                    <p className="text-xs text-orange-600 font-medium">Coût du crédit</p>
                    <p className="text-base font-bold text-orange-900">{formatEur(interestCost)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-600 font-medium">Coût total</p>
                    <p className="text-base font-bold text-slate-900">{formatEur(totalCost)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(showForm || editing) && (
        <EquipmentFormModal
          equipment={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}
